import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Badge,
  DataTable,
  Alert,
  FileUpload,
  ErrorBoundary,
  Checkbox,
  useToast,
} from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import type { ColumnDef } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { GLASS, cn, toneClasses } from '@/constants/design-tokens';
import { ImportReportPanel } from '@/components/features/products/ImportReportPanel';
import type { ImportPreviewRow, ImportStores, ProductImportResponse } from '@/types/product';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import type { CurrencyCode } from '@bbpos/types';
import { getCurrencyDecimals } from '@bbpos/utils';
import { PRODUCT_IMPORT_TEMPLATE_CSV } from './product-import-template';

type PreviewRow = ImportPreviewRow & { id: string };

// ─── Icons ──────────────────────────────────────────────────────────────────

function UploadCloudIcon({ className = 'h-12 w-12' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function XCircleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// ─── CSV Parser ─────────────────────────────────────────────────────────────

// Mirrors the server's per-row rules (products.service processImportJob):
// name, sku, a positive price and a GST slab are all required. The price cell
// is in the currency's major unit (OD6: 199.50 is 199.50 rupees); a symbol, a comma
// or more decimals than the currency has is refused, not rounded.
const VALID_GST_RATES = [0, 5, 12, 18, 28];

// Splits one line like the server's parser (import/csv-parser.util parseCsvLine):
// a quoted field may hold commas, and "" inside quotes is one literal quote.
// QA 3.5.5: a plain split(',') cut a quoted name holding a comma in two.
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') inQuotes = false;
      else current += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') {
      fields.push(current);
      current = '';
    } else current += ch;
  }
  fields.push(current);
  return fields;
}

/** A major-unit money cell → minor units, read as a string like the server's parseMajorAmount; null when refused. */
function parseMajorAmount(cell: string, decimals: number): number | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(cell);
  if (!match) return null;
  const fraction = match[2] ?? '';
  if (fraction.length > decimals) return null;
  return Number(match[1] + fraction.padEnd(decimals, '0'));
}

// CS4-004: like the server (import/csv-parser.util REQUIRED_HEADERS), a file missing a required
// column is refused as a whole — not one "Invalid GST rate" per row with the column never named.
const REQUIRED_HEADERS = ['name', 'sku', 'price', 'gst_rate'];

/** The required columns the CSV's header row lacks (empty when the file has them all). */
export function missingImportColumns(text: string): string[] {
  const firstLine = text.replace(/\r\n?/g, '\n').split('\n').find((line) => line.trim().length > 0) ?? '';
  const headers = parseCsvLine(firstLine).map((h) => h.trim().toLowerCase());
  return REQUIRED_HEADERS.filter((h) => !headers.includes(h));
}

export function parseCsv(text: string, currency: CurrencyCode = 'INR'): ImportPreviewRow[] {
  const decimals = getCurrencyDecimals(currency);
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim().length > 0);
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const nameIdx = headers.indexOf('name');
  const skuIdx = headers.indexOf('sku');
  const categoryIdx = headers.indexOf('category');
  const priceIdx = headers.indexOf('price');
  const gstIdx = headers.indexOf('gst_rate');
  const stockIdx = headers.indexOf('stock_quantity');
  // Like the server: a SKU repeated in the file is refused on every row after its first.
  const firstRowBySku = new Map<string, number>();

  return lines.slice(1).map((line, i) => {
    const cols = parseCsvLine(line).map((c) => c.trim());
    const errs: string[] = [];
    const name = cols[nameIdx] ?? '';
    const sku = cols[skuIdx] ?? '';
    const priceRaw = priceIdx >= 0 ? (cols[priceIdx] ?? '') : '';
    const priceVal = parseMajorAmount(priceRaw, decimals);
    const gstRaw = gstIdx >= 0 ? (cols[gstIdx] ?? '') : '';
    const gstVal = gstRaw === '' ? NaN : Number(gstRaw);

    if (!name) errs.push('Name is required');
    if (!sku) errs.push('SKU is required');
    else if (firstRowBySku.has(sku)) errs.push(`Duplicate SKU in this file: also on row ${firstRowBySku.get(sku)}`);
    else firstRowBySku.set(sku, i + 2);
    if (priceVal === null || priceVal <= 0) errs.push('Invalid price');
    if (!VALID_GST_RATES.includes(gstVal)) errs.push('Invalid GST rate (must be 0, 5, 12, 18 or 28)');

    return {
      row: i + 2,
      name,
      sku,
      category: categoryIdx >= 0 ? cols[categoryIdx] : undefined,
      price: priceVal ?? undefined,
      stock: stockIdx >= 0 ? Number(cols[stockIdx]) || 0 : undefined,
      valid: errs.length === 0,
      errors: errs.length > 0 ? errs : undefined,
    };
  });
}

// ─── Preview table columns ──────────────────────────────────────────────────

function buildPreviewColumns(currency: CurrencyCode): ColumnDef<PreviewRow>[] {
  return [
  {
    key: 'row',
    header: '#',
    width: '60px',
    render: (_v, row) => <span className="text-xs text-neutral-500 tabular-nums">{row.row}</span>,
  },
  {
    key: 'name',
    header: 'Name',
    render: (_v, row) => (
      <span className={cn('text-sm', row.valid ? 'text-neutral-900 dark:text-neutral-100' : 'text-danger-600 dark:text-danger-400')}>
        {row.name || '—'}
      </span>
    ),
  },
  {
    key: 'sku',
    header: 'SKU',
    render: (_v, row) => <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">{row.sku || '—'}</span>,
  },
  {
    key: 'category',
    header: 'Category',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.category ?? '—'}</span>,
  },
  {
    key: 'price',
    header: 'Price',
    numeric: true,
    render: (_v, row) =>
      row.price != null ? (
        <span className="text-sm tabular-nums">{formatPaise(row.price, currency)}</span>
      ) : (
        <span className="text-sm text-neutral-400">—</span>
      ),
  },
  {
    key: 'valid',
    header: 'Status',
    render: (_v, row) =>
      row.valid ? (
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
          <CheckCircleIcon />
          <span className="text-xs font-medium">Valid</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">
          <XCircleIcon />
          <span className="text-xs font-medium">Error</span>
        </span>
      ),
  },
  {
    key: 'errors',
    header: 'Issues',
    render: (_v, row) =>
      row.errors?.length ? (
        <span className="text-xs text-danger-600 dark:text-danger-400">{row.errors.join('; ')}</span>
      ) : (
        <span className="text-xs text-neutral-400">—</span>
      ),
  },
  ];
}

// ─── Main component ─────────────────────────────────────────────────────────

function ProductImportContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const currency = useTenantCurrency();
  const previewColumns = useMemo(() => buildPreviewColumns(currency), [currency]);

  // The stores the import adds its products to; each row becomes one product
  // in the master list. A user with one store has it picked already.
  const myStores = (profile?.locations ?? []).filter((l) => l.is_active);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [allStores, setAllStores] = useState(false);
  const [storeIds, setStoreIds] = useState<string[]>(myStores.length === 1 ? [myStores[0].id] : []);
  const stores: ImportStores = allStores ? 'ALL' : storeIds;
  const hasStores = allStores || storeIds.length > 0;
  // True when the current job is a server-side dry run (validates every row, writes nothing).
  const [isDryRun, setIsDryRun] = useState(false);
  // OD7: off, a row whose SKU already exists is skipped; on, it updates that product.
  const [updateExisting, setUpdateExisting] = useState(false);

  const validCount = preview.filter((r) => r.valid).length;
  const errorCount = preview.filter((r) => !r.valid).length;

  const handleFileUpload = useCallback((files: File[]) => {
    const csvFile = files[0];
    if (!csvFile) return;

    if (!csvFile.name.endsWith('.csv')) {
      // QA 3.5.12: most catalogues live in a spreadsheet — say how to get a CSV out of one.
      toast({
        title: 'Invalid file format. Please upload a .csv file — in Excel or Google Sheets, use Save As / Download as CSV.',
        variant: 'error',
      });
      return;
    }

    if (csvFile.size > 5 * 1024 * 1024) {
      toast({ title: 'File exceeds 5MB limit.', variant: 'error' });
      return;
    }

    setFile(csvFile);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const missing = missingImportColumns(text);
      if (missing.length > 0) {
        setFile(null);
        toast({ title: `This file is missing the column(s): ${missing.join(', ')}`, variant: 'error' });
        return;
      }
      const rows = parseCsv(text, currency);
      setPreview(rows);
    };
    reader.readAsText(csvFile);
  }, [toast, currency]);

  const importMutation = useMutation({
    mutationFn: (dryRun: boolean) => productService.importCsv(file!, stores, dryRun, updateExisting),
    onSuccess: (data: ProductImportResponse, dryRun: boolean) => {
      setIsDryRun(dryRun);
      setImportJobId(data.job_id);
      toast({
        title: dryRun ? 'Validation queued — nothing will be written' : 'Import job queued — check status below',
        variant: 'success',
      });
    },
    onError: (err: Error) => {
      toast({ title: err.message ?? 'Import failed', variant: 'error' });
    },
  });

  // Poll import job status
  const { data: jobStatus } = useQuery({
    queryKey: ['import-job', importJobId],
    queryFn: () => productService.getImportStatus(importJobId!),
    enabled: Boolean(importJobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'RUNNING' ? 2000 : false;
    },
  });

  const isImporting = importMutation.isPending || (jobStatus?.status === 'PENDING' || jobStatus?.status === 'RUNNING');
  const totalRows = jobStatus?.total_rows ?? preview.length;
  const processedRows = jobStatus?.processed_rows ?? 0;
  const progressPct = totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 0;

  function handleDownloadTemplate() {
    const blob = new Blob([PRODUCT_IMPORT_TEMPLATE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleReset() {
    setFile(null);
    setPreview([]);
    setImportJobId(null);
    setIsDryRun(false);
  }

  const previewData: PreviewRow[] = preview.map((r) => ({ ...r, id: String(r.row) }));
  // The server's error rows carry the CSV row number only; the SKU comes from the same file.
  const skuByRow = new Map(preview.map((r) => [r.row, r.sku]));

  return (
    <PageContainer
      title="Import Products"
      subtitle="Bulk import products from a CSV file"
      breadcrumbs={[{ label: 'Products', href: '/products' }, { label: 'Import' }]}
      variant="compact"
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* ─── Upload Area ─────────────────────────────────────────── */}
        {!file && (
          <div className={cn(GLASS, 'p-8')}>
            <div className="text-center mb-6">
              <div className="flex h-20 w-20 mx-auto items-center justify-center rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/10 text-teal-400 dark:text-teal-500 mb-4">
                <UploadCloudIcon />
              </div>
              <h3 className="text-base font-semibold text-neutral-700 dark:text-neutral-200">
                Upload a CSV file to import products
              </h3>
              <p className="text-sm text-neutral-500 mt-1">
                Download the template to get the correct format
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                Type price, cost and store price in {currency} as plain numbers, like 199.50
              </p>
            </div>

            {myStores.length > 1 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Add to stores</p>
                <div className="space-y-2 rounded-lg border border-neutral-100 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/40 p-3">
                  <Checkbox label="All stores" checked={allStores} onChange={setAllStores} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-neutral-100 dark:border-neutral-700 pt-2">
                    {myStores.map((l) => (
                      <Checkbox
                        key={l.id}
                        label={l.location_code ? `${l.name} (${l.location_code})` : l.name}
                        checked={allStores || storeIds.includes(l.id)}
                        disabled={allStores}
                        onChange={(on) =>
                          setStoreIds((s) => (on ? [...s, l.id] : s.filter((id) => id !== l.id)))
                        }
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Each product goes into your product list once and is sold at these stores. An{' '}
                  <code>enable_at</code> column (store codes, separated by ;) picks other stores for its row.
                </p>
              </div>
            )}

            <FileUpload
              accept=".csv"
              maxSize={5 * 1024 * 1024}
              variant="dropzone"
              onUpload={handleFileUpload}
            />

            <div className="flex justify-center mt-4">
              <Button variant="secondary" onClick={handleDownloadTemplate}>
                Download Template
              </Button>
            </div>
          </div>
        )}

        {/* ─── Preview & Import ────────────────────────────────────── */}
        {file && preview.length > 0 && (
          <>
            {/* Summary badges */}
            <div className={cn(GLASS, 'p-4 flex flex-wrap items-center gap-3')}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Badge variant="info">{file.name}</Badge>
                <span className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
                  toneClasses('success').badge,
                )}>
                  {validCount} valid
                </span>
                {errorCount > 0 && (
                  <span className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
                    toneClasses('danger').badge,
                  )}>
                    {errorCount} error{errorCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Clear
              </Button>
            </div>

            {/* Progress bar during import */}
            {isImporting && (
              <div className={cn(GLASS, 'p-5')}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {isDryRun ? 'Validating products…' : 'Importing products…'}
                  </p>
                  <span className="text-sm font-semibold text-teal-600 dark:text-teal-400 tabular-nums">
                    {progressPct}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500 ease-out"
                    style={{ width: `${progressPct}%` }}
                    role="progressbar"
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-2 tabular-nums">
                  {processedRows} of {totalRows} rows processed
                </p>
              </div>
            )}

            {/* Completion states */}
            {jobStatus?.status === 'COMPLETED' && (
              <div className={cn(
                'rounded-xl p-4 flex items-center gap-3',
                'bg-emerald-50/80 dark:bg-emerald-900/20',
              )}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-800/40 text-emerald-600 dark:text-emerald-400">
                  <CheckCircleIcon />
                </div>
                <div>
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    {isDryRun ? 'Validation complete — nothing was written' : 'Import complete'}
                  </p>
                  <p className="text-xs text-neutral-500 tabular-nums">
                    {isDryRun
                      ? `${jobStatus.imported} products would be imported, ${jobStatus.errors} errors`
                      : `${jobStatus.imported} products imported, ${jobStatus.errors} errors`}
                  </p>
                  {!isDryRun && jobStatus.created !== undefined && (
                    <p className="text-xs text-neutral-500 tabular-nums">
                      {`${jobStatus.created} created, ${jobStatus.updated ?? 0} updated, ${jobStatus.unchanged ?? 0} unchanged, ${jobStatus.skipped ?? 0} skipped`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* CS4-003 (OD7): name each row the server refused, and why. */}
            {jobStatus?.status === 'COMPLETED' && (jobStatus.error_details?.length ?? 0) > 0 && (
              <div className={cn(GLASS, 'overflow-hidden animate-fade-in')} data-testid="import-errors">
                <p className="px-4 pt-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                  Rows not imported ({jobStatus.error_details!.length})
                </p>
                <p className="px-4 text-xs text-neutral-500 dark:text-neutral-400">
                  Fix these rows in the file and import it again.
                </p>
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 dark:text-neutral-400">
                      <th className="px-4 py-2 font-medium">Row</th>
                      <th className="px-4 py-2 font-medium">SKU</th>
                      <th className="px-4 py-2 font-medium">Field</th>
                      <th className="px-4 py-2 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobStatus.error_details!.map((e, i) => (
                      <tr
                        key={`${e.row}-${i}`}
                        className="border-t border-neutral-100 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-700 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-800"
                      >
                        <td className="px-4 py-2 tabular-nums text-neutral-500">{e.row}</td>
                        <td className="px-4 py-2 tabular-nums text-neutral-700 dark:text-neutral-300">
                          {skuByRow.get(e.row) || '—'}
                        </td>
                        <td className="px-4 py-2 text-neutral-700 dark:text-neutral-300">{e.field}</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
                            {e.message}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {jobStatus?.status === 'COMPLETED' && jobStatus.report && (
              <ImportReportPanel report={jobStatus.report} currency={currency} />
            )}

            {jobStatus?.status === 'FAILED' && (
              <Alert variant="danger" title="Import failed. Please check your CSV file and try again." />
            )}

            {/* Preview Table */}
            <DataTable<PreviewRow>
              columns={previewColumns}
              data={previewData}
              variant="compact"
              emptyState={{ title: 'No rows to preview' }}
            />

            {/* Actions — pre-import */}
            {!importJobId && (
              <div className="rounded-lg border border-neutral-100 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/40 p-3">
                <Checkbox
                  label="Update existing products"
                  checked={updateExisting}
                  onChange={setUpdateExisting}
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  {updateExisting
                    ? 'A row whose SKU is already in your product list updates that product. A blank cell keeps its current value.'
                    : 'A row whose SKU is already in your product list is skipped. Tick this to update those products instead.'}
                </p>
              </div>
            )}
            {!importJobId && (
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => navigate('/products')}>
                  Cancel
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={handleDownloadTemplate}>
                    Download Template
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => importMutation.mutate(true)}
                    disabled={validCount === 0 || !hasStores || importMutation.isPending}
                  >
                    Validate Only (Dry Run)
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => importMutation.mutate(false)}
                    disabled={validCount === 0 || !hasStores || importMutation.isPending}
                  >
                    {importMutation.isPending ? 'Importing…' : `Import ${validCount} Products`}
                  </Button>
                </div>
              </div>
            )}

            {/* Post-import navigation */}
            {(jobStatus?.status === 'COMPLETED' || jobStatus?.status === 'FAILED') && (
              <div className="flex justify-end gap-2">
                {isDryRun && jobStatus?.status === 'COMPLETED' && (
                  <Button
                    variant="primary"
                    onClick={() => importMutation.mutate(false)}
                    disabled={validCount === 0 || !hasStores || importMutation.isPending}
                  >
                    {`Import ${validCount} Products`}
                  </Button>
                )}
                <Button variant="secondary" onClick={handleReset}>
                  Import Another
                </Button>
                <Button variant="primary" onClick={() => navigate('/products')}>
                  View Products
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
}

export function ProductImportPage() {
  return (
    <ErrorBoundary>
      <ProductImportContent />
    </ErrorBoundary>
  );
}
